import {Formik} from 'formik';
import {Button, Group} from '@mantine/core';
import {MdCheck, MdClear} from 'react-icons/md';
import {I18n} from '@aws-amplify/core';
import {FlexiCancelButton} from '@flexinet/ui-components';
import {FilterFormComponent} from './FilterFormComponent';
import {FilterFormSchema} from './FilterFormSchema';
import {toFormikValidationSchema} from 'zod-formik-adapter';
import {useUsageEventsFilter} from '../../hooks/useUsageEventsFilter';

interface FilterFormProps {
    onApply: () => void;
    onCancel: () => void;
}

interface FilterFormValues {
    meterCode?: string;
    customerId?: string;
    accountId?: string;
    unresolvedCustomer?: string;
    unmetered?: string;
}

export const FilterForm = ({onApply, onCancel}: FilterFormProps) => {
    const {filters, handleSetFilters, handleClearFilters} = useUsageEventsFilter();

    const initialValues: FilterFormValues = {
        meterCode: filters.meterCode || '',
        customerId: filters.customerId || '',
        accountId: filters.accountId || '',
        unresolvedCustomer: filters.unresolvedCustomer || '',
        unmetered: filters.unmetered || '',
    };

    const onSubmit = (values: FilterFormValues) => {
        const clean = Object.entries(values).reduce((acc, [key, value]) => {
            if (value && value !== '') {
                acc[key] = value;
            }
            return acc;
        }, {} as Record<string, string>);

        handleSetFilters(clean);
        onApply();
    };

    return (
        <Formik<FilterFormValues>
            initialValues={initialValues}
            validationSchema={toFormikValidationSchema(FilterFormSchema)}
            onSubmit={onSubmit}
        >
            {({handleSubmit, resetForm}) => (
                <>
                    <FilterFormComponent/>

                    <Group justify="space-between" mt="xl">
                        <FlexiCancelButton onClick={onCancel} data-testid='usage-events-filter-cancel'/>
                        <Group gap="xs">
                            <Button
                                variant="outline"
                                leftSection={<MdClear/>}
                                onClick={() => {
                                    resetForm();
                                    handleClearFilters();
                                }}
                                data-testid='usage-events-filter-clear'
                            >
                                {I18n.get('Clear')}
                            </Button>
                            <Button
                                leftSection={<MdCheck/>}
                                onClick={() => handleSubmit()}
                                data-testid='usage-events-filter-apply'
                            >
                                {I18n.get('Apply')}
                            </Button>
                        </Group>
                    </Group>
                </>
            )}
        </Formik>
    );
};
